
/**
 * Email Service
 * Provides utility functions for sending emails through the Supabase Edge Function
 */

type EmailType = "welcome" | "forgot-password" | "subscription" | "order";

interface SendEmailOptions {
  to: string;
  subject: string;
  emailType: EmailType;
  data: Record<string, any>;
}

export class EmailService {
  private static EDGE_FUNCTION_URL = "https://zcirxkvrqzsljzuoohey.functions.supabase.co/send-email";

  /**
   * Send an email using the send-email edge function
   */
  static async sendEmail({
    to,
    subject,
    emailType,
    data
  }: SendEmailOptions): Promise<boolean> {
    try {
      console.log(`Sending ${emailType} email to ${to}`);
      console.log(`Email data:`, JSON.stringify(data));
      
      const response = await fetch(this.EDGE_FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          to,
          subject,
          emailType,
          data,
        }),
      });

      // Better error handling with response details
      if (!response.ok) {
        let errorMessage = `Email sending failed with status ${response.status}`;
        try {
          const errorData = await response.json();
          console.error("Email service error details:", errorData);
          errorMessage += `: ${errorData.error || JSON.stringify(errorData)}`;
        } catch (e) {
          console.error("Could not parse error response:", e);
        }
        console.error(errorMessage);
        return false;
      }

      const responseData = await response.json();
      console.log(`Successfully sent ${emailType} email to ${to}`, responseData);
      return true;
    } catch (error) {
      console.error("Email service error:", error);
      return false;
    }
  }

  /**
   * Send a welcome email to a new user
   */
  static async sendWelcomeEmail(email: string, firstName: string = ""): Promise<boolean> {
    return this.sendEmail({
      to: email,
      subject: "Welcome to MiniMakes!",
      emailType: "welcome",
      data: { firstName },
    });
  }

  /**
   * Send a password reset email with a direct reset link
   */
  static async sendPasswordResetEmail(email: string, resetLink: string, firstName: string = ""): Promise<boolean> {
    console.log(`Sending password reset email to ${email} with reset link: ${resetLink}`);
    return this.sendEmail({
      to: email,
      subject: "Reset Your MiniMakes Password",
      emailType: "forgot-password",
      data: { 
        resetLink,
        firstName
      },
    });
  }

  /**
   * Send a subscription confirmation email
   */
  static async sendSubscriptionEmail(email: string, plan: string, firstName: string = ""): Promise<boolean> {
    console.log(`Sending subscription confirmation email to ${email} for plan: ${plan}`);
    
    return this.sendEmail({
      to: email,
      subject: `Your MiniMakes ${plan} Subscription is Active!`,
      emailType: "subscription",
      data: { 
        plan, 
        firstName,
        isTest: plan.toLowerCase().includes('test'),
        date: new Date().toLocaleDateString(),
        price: plan.toLowerCase().includes('monthly') ? '$5/month' : '$50/year',
        nextBillingDate: this.getNextBillingDate(plan.toLowerCase().includes('monthly'))
      },
    });
  }

  /**
   * Helper method to calculate next billing date
   */
  private static getNextBillingDate(isMonthly: boolean): string {
    const date = new Date();
    if (isMonthly) {
      date.setMonth(date.getMonth() + 1);
    } else {
      date.setFullYear(date.getFullYear() + 1);
    }
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }

  /**
   * Send an order confirmation email
   */
  static async sendOrderConfirmationEmail(
    email: string, 
    orderId: string, 
    items: Array<{ name: string; price: number }>, 
    total: number,
    firstName: string = ""
  ): Promise<boolean> {
    return this.sendEmail({
      to: email,
      subject: `MiniMakes Order Confirmation #${orderId}`,
      emailType: "order",
      data: { 
        orderId, 
        items, 
        total,
        firstName 
      },
    });
  }
}
